import {useContext} from 'react'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import MoleculeSelectOption from '@s-ui/react-molecule-dropdown-option'
import MoleculeInputField from '@s-ui/react-molecule-input-field'
import MoleculeSelectField from '@s-ui/react-molecule-select-field'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import IconEuro from '@adv-mt/mt-svg-icons/lib/Euro.js'
import InfoOutline from '@adv-mt/mt-svg-icons/lib/InfoOutline'

import {useUtils} from '../../hooks/useUtils.js'

const BASE_CLASS = 'mt-PanelFinancing'
const INPUT_ENTRY_MAX_LENGTH = 6
const MONTHS_PER_YEAR = 12

export const PanelFinancingCalculator = ({
  entryErrorText,
  entryValue,
  handleEntryChange,
  handleOnBlurEntry,
  handleTermsChange,
  hasInputError,
  isProfessional,
  loanCalculations,
  loanCalculationsError = '',
  selectedFareData,
  selectedTerms
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {getInputHelpText} = useUtils()
  const {
    ERRORS: {TransactionNoContentError}
  } = config.get('COMMON')

  const {minEntry, maxEntry, cashPrice, financedPrice, maxEntryToGetDiscount, selectedOption} = loanCalculations || {}

  const termsData = isProfessional ? selectedFareData.availableTerms : loanCalculations.availableTerms
  const isTermsDataEmpty = termsData?.length === 0 || termsData === undefined

  const helpText = getInputHelpText({
    minEntry,
    maxEntry,
    cashPrice,
    entry: entryValue,
    financedPrice,
    isProfessional,
    maxEntryToGetDiscount,
    selectedOption
  })

  const entrySelectLabel = isProfessional ? 'PANEL_FINANCING.B2C.ENTRY_INPUT_LABEL' : 'FINANCING_INFO.CASH_PAYMENT'

  const isNoContentErrorLoanCalculation = loanCalculationsError instanceof TransactionNoContentError

  return (
    <>
      <div className={`${BASE_CLASS}-instalmentCalculatorField`}>
        <MoleculeInputField
          errorText={entryErrorText}
          helpText={!hasInputError && helpText}
          inputMode="decimal"
          label={i18n.t(entrySelectLabel)}
          max={maxEntry}
          maxLength={INPUT_ENTRY_MAX_LENGTH}
          min={minEntry}
          onBlur={handleOnBlurEntry}
          onChange={(e, {value}) => handleEntryChange(value)}
          placeholder={minEntry}
          rightIcon={<IconEuro size="medium" />}
          tabIndex="0"
          type="number"
          value={entryValue}
        />
      </div>
      <div className={`${BASE_CLASS}-instalmentCalculatorField`}>
        <MoleculeSelectField
          iconArrowDown={<IconChevronDown size="medium" />}
          label={i18n.t('COMMON.TERMS')}
          onChange={(e, {value}) => handleTermsChange(value)}
          tabIndex="0"
          value={selectedTerms}
          disabled={isTermsDataEmpty}
        >
          {isTermsDataEmpty ? (
            <MoleculeSelectOption value={selectedTerms}>
              {`${i18n.t('COMMON_N_MONTHS', selectedTerms)} (${i18n.t(
                'COMMON_N_YEARS',
                selectedTerms / MONTHS_PER_YEAR
              )})`}
            </MoleculeSelectOption>
          ) : (
            termsData?.map((value, index) => (
              <MoleculeSelectOption key={index} value={value}>
                {`${i18n.t('COMMON_N_MONTHS', value)} (${i18n.t('COMMON_N_YEARS', value / MONTHS_PER_YEAR)})`}
              </MoleculeSelectOption>
            ))
          )}
        </MoleculeSelectField>
      </div>
      {isNoContentErrorLoanCalculation && (
        <div className={`${BASE_CLASS}-boxInfo`}>
          <span className={`${BASE_CLASS}-boxInfoIcon`}>
            <InfoOutline size={atomIconSizes.medium} />
          </span>
          <span className={`${BASE_CLASS}-boxInfoText`}>
            {i18n.t('PANEL_FINANCING.B2C.ERROR_NO_CONTENT_LOAN_CALCULATIONS')}
          </span>
        </div>
      )}
    </>
  )
}

PanelFinancingCalculator.propTypes = {
  isProfessional: PropTypes.string,
  entryErrorText: PropTypes.string,
  entryValue: PropTypes.string,
  handleEntryChange: PropTypes.func,
  handleTermsChange: PropTypes.func,
  handleOnBlurEntry: PropTypes.func,
  hasInputError: PropTypes.bool,
  loanCalculations: PropTypes.object,
  loanCalculationsError: PropTypes.string,
  selectedFareData: PropTypes.object,
  selectedTerms: PropTypes.string
}
